// src/components/WebsiteContent.jsx
//
// What guests see on the Vintara Stays website for a resort - separate from
// "Resorts" (room categories, rates, commission, owner, integrations) and
// "Rate Management" (pricing). Moved out of a buried tab on the Resorts page
// into its own menu item so website-facing content isn't mixed in with
// operational/booking data.
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { menuPortalTarget, menuPosition, themedSelectStyles } from "../utils/reactSelectTheme";
import ToastContainer, { useToast } from "./common/Toast";
import {
  fetchResorts,
  fetchResortById,
  updateResortDetails,
  uploadResortPhoto,
  deleteResortPhoto,
  uploadRoomCategoryPhoto,
  deleteRoomCategoryPhoto,
  publicMediaUrl,
  addResortFaq,
  updateResortFaq,
  deleteResortFaq,
  moveResortFaq,
} from "../services/ResortDetailsService";
import "./WebsiteContent.css";

// Fixed list, not a backend enum - adding/renaming an amenity is a
// frontend-only change, not a redeploy (see Resort.amenities' javadoc).
const AMENITIES = [
  "WiFi", "Swimming Pool", "Parking", "Restaurant", "Bonfire",
  "Beach Access", "Power Backup", "Hot Water", "Air Conditioning", "Television",
  "Room Service", "Housekeeping", "Indoor Games", "Kids Play Area", "Pet Friendly",
  "Barbecue / BBQ", "Banquet Hall", "CCTV Security", "Elevator / Lift",
  "Doctor on Call", "Travel Desk", "Laundry Service", "Couple Friendly",
  "Private Pool", "Garden / Lawn", "Bonfire Dining", "Beach Chairs & Umbrellas",
  "Outdoor Games", "Airport Transfer",
];

// Must match the backend MealPlan/CancellationPolicy enum names exactly.
const MEAL_PLAN_OPTIONS = [
  { value: "NOT_INCLUDED", label: "Not included" },
  { value: "BREAKFAST_ONLY", label: "Breakfast included" },
  { value: "BREAKFAST_DINNER", label: "Breakfast & dinner included (MAP)" },
  { value: "ALL_MEALS", label: "All meals included (AP)" },
];

const CANCELLATION_POLICY_OPTIONS = [
  { value: "FLEXIBLE", label: "Free cancellation up to 24 hours before check-in" },
  { value: "MODERATE", label: "Free cancellation up to 48 hours before check-in" },
  { value: "STRICT", label: "Non-refundable" },
  { value: "CUSTOM", label: "Custom - see additional notes" },
];

// Room-level amenities, shown on each room type's microsite card -
// distinct from AMENITIES above (property-wide features like Swimming
// Pool/Restaurant don't belong on an individual room card, and these
// don't belong on the property-wide list either). Same fixed-list-in-
// frontend convention, see RoomCategory.amenities' javadoc.
const ROOM_AMENITIES = [
  "AC", "Fan", "Attached Bathroom", "Balcony", "Sea View", "Garden View",
  "Television", "Mini Fridge", "Hot Water", "Study Table", "Wardrobe",
  "Extra Bed Available", "Smoking Allowed",
  "Electric Kettle", "Mosquito Net", "In-Room Safe", "Private Pool",
  "Bathtub", "Pool View",
];

// Fixed list, not a backend enum - same reasoning as AMENITIES above. These
// are marketing/vibe framing for the guest website's listing card, distinct
// from AMENITIES (factual property features the WhatsApp bot answers
// questions from).
const VIBE_TAGS = [
  "Pet Friendly", "Couple Friendly", "Workation Ready", "Bonfire Nights",
  "Instagrammable", "Group Getaways", "Sunset Views",
];

const LISTING_BADGE_OPTIONS = [
  { value: "", label: "No badge" },
  { value: "Popular", label: "Popular" },
  { value: "Instagrammable", label: "Instagrammable" },
  { value: "Workation Ready", label: "Workation Ready" },
  { value: "Group Getaways", label: "Group Getaways" },
];

const TABS = [
  { key: "OVERVIEW", label: "Overview" },
  { key: "ROOMS", label: "Room Types" },
  { key: "POLICIES", label: "Amenities & Policies" },
  { key: "FAQS", label: "FAQs" },
];

const WebsiteContent = () => {
  const [resorts, setResorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS[0].key);
  const { toasts, showToast, dismissToast } = useToast();

  const [selectedResort, setSelectedResort] = useState(null); // react-select option {value, label}
  const [amenities, setAmenities] = useState(new Set());
  // Up to 3 of `amenities` above, chosen by staff to show on the guest-site
  // listing card's checkmark row instead of whichever 3 happen to sort
  // first alphabetically - see Resort.featuredAmenities's javadoc.
  const [featuredAmenities, setFeaturedAmenities] = useState(new Set());
  const [checkInTime, setCheckInTime] = useState("");
  const [checkOutTime, setCheckOutTime] = useState("");
  const [poolHoursStart, setPoolHoursStart] = useState("");
  const [poolHoursEnd, setPoolHoursEnd] = useState("");
  const [quietHoursNote, setQuietHoursNote] = useState("");
  const [mealPlan, setMealPlan] = useState(null);
  const [cancellationPolicy, setCancellationPolicy] = useState(null);
  const [notes, setNotes] = useState("");

  const [tagline, setTagline] = useState("");
  const [landmark, setLandmark] = useState("");
  const [vibeTags, setVibeTags] = useState(new Set());
  const [listingBadge, setListingBadge] = useState(LISTING_BADGE_OPTIONS[0]);
  const [resortPhotos, setResortPhotos] = useState([]); // [{id, path}]
  const [categories, setCategories] = useState([]); // this resort's room categories, as loaded
  // keyed by category id: { [categoryId]: { description, baseOccupancy, maxOccupancy } }
  const [categoryContent, setCategoryContent] = useState({});
  // keyed by category id: { [categoryId]: [{id, path}] }
  const [categoryPhotos, setCategoryPhotos] = useState({});
  const [uploadingKey, setUploadingKey] = useState(null); // "resort" or a category id, while a photo upload is in flight

  // Resort FAQ entries - free-text question/answer pairs, saved
  // immediately on add/edit/delete/move, same as photos above (not part
  // of handleSave's batched payload). [{id, question, answer}], already
  // in display order.
  const [faqs, setFaqs] = useState([]);
  const [newFaqQuestion, setNewFaqQuestion] = useState("");
  const [newFaqAnswer, setNewFaqAnswer] = useState("");
  const [editingFaqId, setEditingFaqId] = useState(null);
  const [editFaqQuestion, setEditFaqQuestion] = useState("");
  const [editFaqAnswer, setEditFaqAnswer] = useState("");
  const [faqBusy, setFaqBusy] = useState(false);

  const loadResorts = async () => {
    try {
      setLoading(true);
      const data = await fetchResorts();
      setResorts(data || []);
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to load resorts", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResorts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Active resorts only - website content (amenities, photos, vibe tags,
  // guest-facing copy) is only meaningful for a resort guests can actually
  // book. An inactive/removed resort can still be reached directly via
  // fetchResortById below if one's already selected when its status
  // changes (not force-cleared out from under an admin mid-edit) - this
  // only narrows the picker's own list.
  const resortOptions = useMemo(
    () => resorts.filter((r) => r.status === "ACTIVE").map((r) => ({ value: r.id, label: r.name })),
    [resorts]
  );

  // Populate the form whenever a different resort is picked. Fetches that
  // one resort's full detail (including photos) rather than reading from
  // the bulk `resorts` list - see fetchResortById's comment for why: the
  // bulk list is shared with screens that never need photos, so it
  // deliberately omits them.
  useEffect(() => {
    if (!selectedResort) return;
    let cancelled = false;
    setLoadingDetail(true);
    setActiveTab(TABS[0].key);
    fetchResortById(selectedResort.value)
      .then((resort) => {
        if (cancelled || !resort) return;
        setAmenities(new Set(resort.amenities || []));
        setFeaturedAmenities(new Set(resort.featuredAmenities || []));
        setCheckInTime(resort.checkInTime || "");
        setCheckOutTime(resort.checkOutTime || "");
        setPoolHoursStart(resort.poolHoursStart || "");
        setPoolHoursEnd(resort.poolHoursEnd || "");
        setQuietHoursNote(resort.quietHoursNote || "");
        setMealPlan(MEAL_PLAN_OPTIONS.find((o) => o.value === resort.mealPlan) || null);
        setCancellationPolicy(CANCELLATION_POLICY_OPTIONS.find((o) => o.value === resort.cancellationPolicy) || null);
        setNotes(resort.guestFacingInfo || "");

        setTagline(resort.tagline || "");
        setLandmark(resort.landmark || "");
        setVibeTags(new Set(resort.vibeTags || []));
        setListingBadge(LISTING_BADGE_OPTIONS.find((o) => o.value === (resort.listingBadge || "")) || LISTING_BADGE_OPTIONS[0]);
        setResortPhotos(resort.photos || []);
        setFaqs(resort.faqs || []);
        setEditingFaqId(null);
        setNewFaqQuestion("");
        setNewFaqAnswer("");

        const cats = resort.categories || [];
        setCategories(cats);
        const nextContent = {};
        const nextPhotos = {};
        cats.forEach((c) => {
          nextContent[c.id] = {
            description: c.description || "",
            baseOccupancy: c.baseOccupancy ?? "",
            maxOccupancy: c.maxOccupancy ?? "",
            extraPersonRate: c.extraPersonRate ?? "",
            amenities: new Set(c.amenities || []),
          };
          nextPhotos[c.id] = c.photos || [];
        });
        setCategoryContent(nextContent);
        setCategoryPhotos(nextPhotos);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        showToast(err.message || "Failed to load resort details", "danger");
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResort]);

  const toggleAmenity = (name) => {
    setAmenities((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
    // Unchecking a general amenity that was also featured must drop it
    // from featured too - a featured item has to stay a subset of the
    // checked ones (enforced again server-side, this just keeps the UI
    // from showing a star on something that's no longer even checked).
    setFeaturedAmenities((prev) => {
      if (!prev.has(name)) return prev;
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  // Star toggle next to each checked amenity - capped at 3, same limit
  // ResortService enforces server-side. Only ever called for an amenity
  // that's already checked (the UI only renders the star for those).
  const toggleFeaturedAmenity = (name) => {
    setFeaturedAmenities((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else if (next.size < 3) {
        next.add(name);
      }
      return next;
    });
  };

  const toggleVibeTag = (name) => {
    setVibeTags((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const setCategoryField = (categoryId, field, value) => {
    setCategoryContent((prev) => ({
      ...prev,
      [categoryId]: { ...prev[categoryId], [field]: value },
    }));
  };

  const toggleCategoryAmenity = (categoryId, name) => {
    setCategoryContent((prev) => {
      const current = prev[categoryId] || {};
      const next = new Set(current.amenities);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return { ...prev, [categoryId]: { ...current, amenities: next } };
    });
  };

  const handleResortPhotoUpload = async (file) => {
    if (!selectedResort || !file) return;
    setUploadingKey("resort");
    try {
      const photo = await uploadResortPhoto(selectedResort.value, file);
      setResortPhotos((prev) => [...prev, photo]);
    } catch (err) {
      console.error(err);
      showToast(err.message || "Photo upload failed", "danger");
    } finally {
      setUploadingKey(null);
    }
  };

  const handleResortPhotoDelete = async (photoId) => {
    if (!selectedResort) return;
    try {
      await deleteResortPhoto(selectedResort.value, photoId);
      setResortPhotos((prev) => prev.filter((p) => p.id !== photoId));
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to remove photo", "danger");
    }
  };

  const handleCategoryPhotoUpload = async (categoryId, file) => {
    if (!selectedResort || !file) return;
    setUploadingKey(categoryId);
    try {
      const photo = await uploadRoomCategoryPhoto(selectedResort.value, categoryId, file);
      setCategoryPhotos((prev) => ({ ...prev, [categoryId]: [...(prev[categoryId] || []), photo] }));
    } catch (err) {
      console.error(err);
      showToast(err.message || "Photo upload failed", "danger");
    } finally {
      setUploadingKey(null);
    }
  };

  const handleCategoryPhotoDelete = async (categoryId, photoId) => {
    if (!selectedResort) return;
    try {
      await deleteRoomCategoryPhoto(selectedResort.value, categoryId, photoId);
      setCategoryPhotos((prev) => ({
        ...prev,
        [categoryId]: (prev[categoryId] || []).filter((p) => p.id !== photoId),
      }));
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to remove photo", "danger");
    }
  };

  const handleAddFaq = async () => {
    if (!selectedResort || !newFaqQuestion.trim() || !newFaqAnswer.trim() || faqBusy) return;
    setFaqBusy(true);
    try {
      const faq = await addResortFaq(selectedResort.value, {
        question: newFaqQuestion.trim(),
        answer: newFaqAnswer.trim(),
      });
      setFaqs((prev) => [...prev, faq]);
      setNewFaqQuestion("");
      setNewFaqAnswer("");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to add FAQ", "danger");
    } finally {
      setFaqBusy(false);
    }
  };

  const handleStartEditFaq = (faq) => {
    setEditingFaqId(faq.id);
    setEditFaqQuestion(faq.question);
    setEditFaqAnswer(faq.answer);
  };

  const handleCancelEditFaq = () => {
    setEditingFaqId(null);
    setEditFaqQuestion("");
    setEditFaqAnswer("");
  };

  const handleSaveEditFaq = async () => {
    if (!selectedResort || !editingFaqId || !editFaqQuestion.trim() || !editFaqAnswer.trim() || faqBusy) return;
    setFaqBusy(true);
    try {
      const updated = await updateResortFaq(selectedResort.value, editingFaqId, {
        question: editFaqQuestion.trim(),
        answer: editFaqAnswer.trim(),
      });
      setFaqs((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
      handleCancelEditFaq();
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to save FAQ", "danger");
    } finally {
      setFaqBusy(false);
    }
  };

  const handleDeleteFaq = async (faqId) => {
    if (!selectedResort) return;
    try {
      await deleteResortFaq(selectedResort.value, faqId);
      setFaqs((prev) => prev.filter((f) => f.id !== faqId));
      if (editingFaqId === faqId) handleCancelEditFaq();
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to delete FAQ", "danger");
    }
  };

  const handleMoveFaq = async (faqId, direction) => {
    if (!selectedResort || faqBusy) return;
    setFaqBusy(true);
    try {
      const reordered = await moveResortFaq(selectedResort.value, faqId, direction);
      setFaqs(reordered);
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to reorder FAQs", "danger");
    } finally {
      setFaqBusy(false);
    }
  };

  const handleSave = async () => {
    if (!selectedResort) return;

    // Caught here too, not just server-side, so a mistake is flagged
    // immediately instead of after a round trip - the backend still
    // enforces this itself (ResortService.updateResortDetails) since this
    // page is never the only caller of that endpoint.
    for (const c of categories) {
      const base = categoryContent[c.id]?.baseOccupancy;
      const max = categoryContent[c.id]?.maxOccupancy;
      if (base !== "" && max !== "" && base != null && max != null && Number(max) < Number(base)) {
        showToast(`"${c.name}": max occupancy (${max}) can't be less than base occupancy (${base}).`, "danger");
        return;
      }
    }

    setSaving(true);
    try {
      await updateResortDetails(selectedResort.value, {
        amenities: Array.from(amenities),
        featuredAmenities: Array.from(featuredAmenities),
        checkInTime: checkInTime || null,
        checkOutTime: checkOutTime || null,
        poolHoursStart: poolHoursStart || null,
        poolHoursEnd: poolHoursEnd || null,
        quietHoursNote: quietHoursNote || null,
        mealPlan: mealPlan?.value || null,
        cancellationPolicy: cancellationPolicy?.value || null,
        guestFacingInfo: notes,
        tagline: tagline || null,
        landmark: landmark || null,
        vibeTags: Array.from(vibeTags),
        listingBadge: listingBadge?.value || null,
        roomCategoryContent: categories.map((c) => ({
          id: c.id,
          description: categoryContent[c.id]?.description || null,
          baseOccupancy: categoryContent[c.id]?.baseOccupancy === "" ? null : Number(categoryContent[c.id]?.baseOccupancy),
          maxOccupancy: categoryContent[c.id]?.maxOccupancy === "" ? null : Number(categoryContent[c.id]?.maxOccupancy),
          extraPersonRate: categoryContent[c.id]?.extraPersonRate === "" ? null : Number(categoryContent[c.id]?.extraPersonRate),
          amenities: Array.from(categoryContent[c.id]?.amenities || []),
        })),
      });
      showToast("Website content saved", "success");
      await loadResorts();
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to save website content", "danger");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="resorts-page-container website-content-page" style={{ padding: "0px 20px 20px 20px", boxSizing: "border-box" }}>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      <div className="vt-page-header">
        <div>
          <h2>Website Content</h2>
          <p className="field-hint website-content-intro">
            What guests see on the Vintara Stays website for this resort. Room categories, counts and rates are
            still managed on Resorts and Rate Management — this page is just the guest-facing copy and photos.
          </p>
        </div>
      </div>

      <div className="website-content-selector">
        <Select
          options={resortOptions}
          value={selectedResort}
          onChange={setSelectedResort}
          placeholder={loading ? "Loading resorts..." : "Select a resort..."}
          isLoading={loading}
          menuPortalTarget={menuPortalTarget}
          menuPosition={menuPosition}
          styles={themedSelectStyles()}
        />
      </div>

      {selectedResort && loadingDetail && <p className="field-hint">Loading resort details...</p>}

      {selectedResort && !loadingDetail && (
        <>
          <div className="resorts-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`resorts-tab-btn ${activeTab === tab.key ? "resorts-tab-btn--active" : ""}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="resort-details-form">
            {activeTab === "OVERVIEW" && (
              <>
                <div className="resort-details-section">
                  <h4>Listing basics</h4>
                  <div className="form-row form-row--2col">
                    <div className="form-field">
                      <label>Landmark / distance</label>
                      <input
                        type="text"
                        placeholder="5 min walk to Varsoli Beach"
                        value={landmark}
                        onChange={(e) => setLandmark(e.target.value)}
                      />
                      <p className="field-hint">Shown on the listing card right under the resort name, e.g. "3 min walk to Anjuna Beach".</p>
                    </div>
                    <div className="form-field">
                      <label>Listing badge</label>
                      <Select
                        options={LISTING_BADGE_OPTIONS}
                        value={listingBadge}
                        onChange={setListingBadge}
                        menuPortalTarget={menuPortalTarget}
                        menuPosition={menuPosition}
                        styles={themedSelectStyles()}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-field">
                      <label>About this resort</label>
                      <textarea
                        rows={4}
                        placeholder="A restored 1920s colonial bungalow steps from Varsoli Beach — book a room, or the whole top floor for the group."
                        value={tagline}
                        onChange={(e) => setTagline(e.target.value)}
                      />
                      <p className="field-hint">Shown on the microsite as the longer marketing description.</p>
                    </div>
                  </div>
                  <div className="form-field" style={{ marginBottom: "var(--space-5)" }}>
                    <label>Vibe tags</label>
                    <div className="resort-details-chip-row">
                      {VIBE_TAGS.map((name) => (
                        <span
                          key={name}
                          className={`resort-details-chip${vibeTags.has(name) ? " on" : ""}`}
                          onClick={() => toggleVibeTag(name)}
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="resort-details-section">
                  <h4>Resort photos</h4>
                  <p className="field-hint">First photo is the hero shot on the listing card and microsite.</p>
                  <div className="resort-details-photo-grid">
                    {resortPhotos.map((photo) => (
                      <div
                        key={photo.id}
                        className="resort-details-photo-tile"
                        style={{ backgroundImage: `url(${publicMediaUrl(photo.path)})` }}
                      >
                        <button
                          type="button"
                          className="resort-details-photo-remove"
                          onClick={() => handleResortPhotoDelete(photo.id)}
                        >
                          &#10005;
                        </button>
                      </div>
                    ))}
                    <label className="resort-details-photo-dropzone">
                      <span className="plus">+</span>
                      {uploadingKey === "resort" ? "Uploading..." : "Add photos"}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        disabled={uploadingKey === "resort"}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          handleResortPhotoUpload(file);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </div>
                </div>
              </>
            )}

            {activeTab === "ROOMS" && (
              <div className="resort-details-section">
                <p className="field-hint">
                  Categories, room counts and rates come from this resort's main setup form - this is just each
                  one's guest-facing description, occupancy and photos. Max occupancy is enforced on the booking
                  flow - guests can't select more people than their chosen rooms can hold.
                </p>
                {categories.length === 0 && <p className="field-hint">No room categories set up yet for this resort.</p>}
                <div className="resort-details-room-type-list">
                  {categories.map((cat) => (
                    <div key={cat.id} className="resort-details-room-type-card">
                      <div className="resort-details-room-type-head">
                        <span className="name">{cat.name}</span>
                        <span className="meta">{cat.totalRooms} rooms</span>
                      </div>
                      <div className="form-row form-row--3col">
                        <div className="form-field">
                          <label>Description</label>
                          <input
                            type="text"
                            placeholder="Garden-facing, queen bed, ensuite bath"
                            value={categoryContent[cat.id]?.description || ""}
                            onChange={(e) => setCategoryField(cat.id, "description", e.target.value)}
                          />
                        </div>
                        <div className="form-field">
                          <label>Base occupancy</label>
                          <input
                            type="number"
                            min="1"
                            placeholder="2"
                            value={categoryContent[cat.id]?.baseOccupancy ?? ""}
                            onChange={(e) => setCategoryField(cat.id, "baseOccupancy", e.target.value)}
                          />
                        </div>
                        <div className="form-field">
                          <label>Max occupancy</label>
                          <input
                            type="number"
                            min="1"
                            placeholder="4"
                            value={categoryContent[cat.id]?.maxOccupancy ?? ""}
                            onChange={(e) => setCategoryField(cat.id, "maxOccupancy", e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-field">
                          <label>Extra person rate (₹/night per extra adult)</label>
                          <input
                            type="number"
                            min="0"
                            placeholder="Leave blank for no extra-guest charge"
                            value={categoryContent[cat.id]?.extraPersonRate ?? ""}
                            onChange={(e) => setCategoryField(cat.id, "extraPersonRate", e.target.value)}
                          />
                        </div>
                      </div>
                      <p className="field-hint">
                        Charged per night for each adult booked into this category beyond base occupancy. Leave
                        blank to never surcharge this category, however many guests are added.
                      </p>
                      <div className="resort-details-amenities-grid">
                        {ROOM_AMENITIES.map((name) => (
                          <label key={name} className="resort-details-amenity-checkbox">
                            <input
                              type="checkbox"
                              checked={categoryContent[cat.id]?.amenities?.has(name) || false}
                              onChange={() => toggleCategoryAmenity(cat.id, name)}
                            />
                            {name}
                          </label>
                        ))}
                      </div>
                      <div className="resort-details-photo-grid">
                        {(categoryPhotos[cat.id] || []).map((photo) => (
                          <div
                            key={photo.id}
                            className="resort-details-photo-tile"
                            style={{ backgroundImage: `url(${publicMediaUrl(photo.path)})` }}
                          >
                            <button
                              type="button"
                              className="resort-details-photo-remove"
                              onClick={() => handleCategoryPhotoDelete(cat.id, photo.id)}
                            >
                              &#10005;
                            </button>
                          </div>
                        ))}
                        <label className="resort-details-photo-dropzone">
                          <span className="plus">+</span>
                          {uploadingKey === cat.id ? "Uploading..." : "Add photos"}
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            disabled={uploadingKey === cat.id}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              handleCategoryPhotoUpload(cat.id, file);
                              e.target.value = "";
                            }}
                          />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "POLICIES" && (
              <>
                <div className="resort-details-section">
                  <h4>Amenities</h4>
                  <p className="field-hint">
                    Click the star on up to 3 checked amenities to feature them on the listing card ({featuredAmenities.size}/3 selected) - otherwise the card falls back to the first 3 alphabetically.
                  </p>
                  <div className="resort-details-amenities-grid">
                    {AMENITIES.map((name) => {
                      const checked = amenities.has(name);
                      const featured = featuredAmenities.has(name);
                      return (
                        <label key={name} className="resort-details-amenity-checkbox">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleAmenity(name)}
                          />
                          {name}
                          {checked && (
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); toggleFeaturedAmenity(name); }}
                              disabled={!featured && featuredAmenities.size >= 3}
                              title={featured ? "Featured on listing card - click to remove" : "Feature on listing card"}
                              style={{
                                marginLeft: 6,
                                background: "none",
                                border: "none",
                                cursor: !featured && featuredAmenities.size >= 3 ? "not-allowed" : "pointer",
                                color: featured ? "#c9941f" : "#c9c0ae",
                                fontSize: 15,
                                padding: 0,
                                lineHeight: 1,
                              }}
                            >
                              {featured ? "★" : "☆"}
                            </button>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="resort-details-section">
                  <h4>Timings & Policy</h4>
                  <div className="form-row form-row--2col">
                    <div className="form-field">
                      <label>Check-in Time</label>
                      <input type="time" value={checkInTime} onChange={(e) => setCheckInTime(e.target.value)} />
                    </div>
                    <div className="form-field">
                      <label>Check-out Time</label>
                      <input type="time" value={checkOutTime} onChange={(e) => setCheckOutTime(e.target.value)} />
                    </div>
                  </div>
                  <div className="form-row form-row--2col">
                    <div className="form-field">
                      <label>Pool Hours Start</label>
                      <input type="time" value={poolHoursStart} onChange={(e) => setPoolHoursStart(e.target.value)} />
                    </div>
                    <div className="form-field">
                      <label>Pool Hours End</label>
                      <input type="time" value={poolHoursEnd} onChange={(e) => setPoolHoursEnd(e.target.value)} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-field">
                      <label>Quiet Hours Note</label>
                      <input type="text" placeholder="Music till 9 PM" value={quietHoursNote} onChange={(e) => setQuietHoursNote(e.target.value)} />
                    </div>
                  </div>
                  <div className="form-row form-row--2col">
                    <div className="form-field">
                      <label>Meal Plan</label>
                      <Select
                        options={MEAL_PLAN_OPTIONS}
                        value={mealPlan}
                        onChange={setMealPlan}
                        isClearable
                        placeholder="Select meal plan..."
                        menuPortalTarget={menuPortalTarget}
                        menuPosition={menuPosition}
                        styles={themedSelectStyles()}
                      />
                    </div>
                    <div className="form-field">
                      <label>Cancellation Policy</label>
                      <Select
                        options={CANCELLATION_POLICY_OPTIONS}
                        value={cancellationPolicy}
                        onChange={setCancellationPolicy}
                        isClearable
                        placeholder="Select cancellation policy..."
                        menuPortalTarget={menuPortalTarget}
                        menuPosition={menuPosition}
                        styles={themedSelectStyles()}
                      />
                    </div>
                  </div>
                </div>

                <div className="resort-details-section">
                  <h4>Additional Notes</h4>
                  <p className="field-hint">
                    Anything the fields above don't cover - what's nearby, special instructions, etc. Also grounds
                    the WhatsApp chatbot's FAQ answers for this resort.
                  </p>
                  <textarea
                    rows={4}
                    placeholder="e.g. 5-minute walk from Kashid Beach, free parking for 2-wheelers and 4-wheelers..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>

                <div className="resort-details-section">
                  <h4>Reviews</h4>
                  <p className="field-hint">
                    Guest reviews on the website are pulled automatically from this resort's Google Business
                    Profile - nothing to manage here. Visibility and responses are handled on the Reviews page.
                  </p>
                </div>
              </>
            )}

            {activeTab === "FAQS" && (
              <div className="resort-details-section">
                <h4>Frequently asked questions</h4>
                <p className="field-hint">
                  Free-text questions specific to this resort, e.g. "How far is the beach?" - shown on the
                  microsite and marked up as FAQ schema for search. Each change below saves immediately.
                </p>

                {faqs.length === 0 && <p className="field-hint">No FAQs added yet for this resort.</p>}

                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)", marginBottom: "var(--space-5)" }}>
                  {faqs.map((faq, idx) => (
                    <div
                      key={faq.id}
                      style={{ border: "1px solid var(--gray-300)", borderRadius: "var(--radius-sm)", padding: "var(--space-4)", background: "#fff" }}
                    >
                      {editingFaqId === faq.id ? (
                        <>
                          <div className="form-field" style={{ marginBottom: "var(--space-3)" }}>
                            <label>Question</label>
                            <input type="text" value={editFaqQuestion} onChange={(e) => setEditFaqQuestion(e.target.value)} />
                          </div>
                          <div className="form-field" style={{ marginBottom: "var(--space-3)" }}>
                            <label>Answer</label>
                            <textarea rows={3} value={editFaqAnswer} onChange={(e) => setEditFaqAnswer(e.target.value)} />
                          </div>
                          <div className="button-group">
                            <button type="button" className="vt-btn vt-btn-primary" disabled={faqBusy} onClick={handleSaveEditFaq}>
                              {faqBusy ? "Saving..." : "Save"}
                            </button>
                            <button type="button" className="vt-btn vt-btn-purple" onClick={handleCancelEditFaq}>
                              Cancel
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-3)" }}>
                            <div>
                              <b>{faq.question}</b>
                              <p style={{ margin: "6px 0 0", color: "var(--gray-700)" }}>{faq.answer}</p>
                            </div>
                            <div style={{ display: "flex", gap: "var(--space-2)", flexShrink: 0 }}>
                              <button
                                type="button"
                                className="edit-btn"
                                disabled={faqBusy || idx === 0}
                                onClick={() => handleMoveFaq(faq.id, "up")}
                                title="Move up"
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className="edit-btn"
                                disabled={faqBusy || idx === faqs.length - 1}
                                onClick={() => handleMoveFaq(faq.id, "down")}
                                title="Move down"
                              >
                                ↓
                              </button>
                              <button type="button" className="edit-btn" onClick={() => handleStartEditFaq(faq)}>
                                Edit
                              </button>
                              <button type="button" className="vt-btn vt-btn-danger" onClick={() => handleDeleteFaq(faq.id)}>
                                Delete
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                <div style={{ border: "1px dashed var(--gray-400)", borderRadius: "var(--radius-sm)", padding: "var(--space-4)" }}>
                  <h4 style={{ marginTop: 0 }}>Add a FAQ</h4>
                  <div className="form-field" style={{ marginBottom: "var(--space-3)" }}>
                    <label>Question</label>
                    <input
                      type="text"
                      placeholder="How far is the beach from the property?"
                      value={newFaqQuestion}
                      onChange={(e) => setNewFaqQuestion(e.target.value)}
                    />
                  </div>
                  <div className="form-field" style={{ marginBottom: "var(--space-3)" }}>
                    <label>Answer</label>
                    <textarea
                      rows={3}
                      placeholder="It's a 5-minute walk - about 400 metres."
                      value={newFaqAnswer}
                      onChange={(e) => setNewFaqAnswer(e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="vt-btn vt-btn-primary"
                    disabled={faqBusy || !newFaqQuestion.trim() || !newFaqAnswer.trim()}
                    onClick={handleAddFaq}
                  >
                    {faqBusy ? "Saving..." : "+ Add FAQ"}
                  </button>
                </div>
              </div>
            )}

            <div className="button-group">
              <button type="button" className="vt-btn vt-btn-primary" disabled={saving} onClick={handleSave}>
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default WebsiteContent;
